import api from '../../lib/api';
import { showErrorToast } from '../../utils/commonUtils';

/**
 * Experiment Design API
 * 
 * Important: Sample Size Behavior
 * - Leaf designs (designs without children): Can have a numeric sample_size value
 * - Parent designs (designs with children): Always have sample_size = null in the database
 * - The total sample size for parent designs is calculated by summing all leaf descendants
 * - This ensures data consistency and prevents double-counting of samples
 */

// Type definitions for additional samples based on API specification
export interface AdditionalSample {
    additional_sample_id?: number | null;
    sample_type?: string | null;
    sample_source?: string | null;
    sample_method?: string | null;
    sample_timepoint?: number[] | null;  // Array of numbers
    brain_region?: string[] | null;      // Array of strings
    amount?: number | null;
    amount_units?: string | null;
    number_of_samples?: number | null;
    experiment_design_id?: number | null;
}

// Type definitions for interventions based on API specification
export interface ExperimentDesignIntervention {
    experiment_design_intervention_id?: number | null;
    name?: string | null;
    administration_type?: string | null;
    dose_amount?: number | null;
    dose_amount_units?: string | null;
    timepoint?: number | null;
    notes?: string | null;
    experiment_design_id?: number | null;
}

// Type definitions based on API specification
export interface ExperimentDesignCreate {
    name: string;
    owner?: string | null;
    status?: string | null;
    target_disease?: string | null;
    updated_by?: string | null;
    parent_experiment_design_id?: number | null;
    sample_size?: number | null; // Only used for leaf designs (designs without children)
    deviations?: string | null;
    deviations_required?: boolean | null;
    species?: string | null;
    partner?: string | null;
    intervention_required?: boolean | null;
    interventions?: ExperimentDesignIntervention[] | null;
    additional_samples_required?: boolean | null; // Whether to use additional samples for this experiment design
    additional_samples?: AdditionalSample[] | null; // Additional samples for this experiment design
}

export interface ExperimentDesignCreateWithChildren extends ExperimentDesignCreate {
    children?: ExperimentDesignCreate[];
}

export interface ExperimentDesignUpdate {
    name?: string | null;
    owner?: string | null;
    status?: string | null;
    target_disease?: string | null;
    updated_by?: string | null;
    parent_experiment_design_id?: number | null;
    sample_size?: number | null; // Only used for leaf designs (designs without children)
    deviations?: string | null;
    deviations_required?: boolean | null;
    species?: string | null;
    partner?: string | null;
    intervention_required?: boolean | null;
    interventions?: ExperimentDesignIntervention[] | null;
    additional_samples_required?: boolean | null; // Whether to use additional samples for this experiment design
    additional_samples?: AdditionalSample[] | null; // Additional samples for this experiment design
}

export interface ExperimentDesignFull {
    experiment_design_id: number;
    name: string;
    owner?: string | null;
    status?: string | null;
    target_disease?: string | null;
    updated_by?: string | null;
    parent_experiment_design_id?: number | null;
    sample_size?: number | null; // Null for parent designs (with children), numeric for leaf designs
    updated_at?: string | null;
    children?: ExperimentDesignFull[]; // For hierarchical display
    deviations?: string | null;
    deviations_required?: boolean | null;
    species?: string | null;
    partner?: string | null;
    intervention_required?: boolean | null;
    interventions?: ExperimentDesignIntervention[];
    additional_samples_required?: boolean | null; // Whether to use additional samples for this experiment design
    additional_samples?: AdditionalSample[]; // Additional samples for this experiment design
}

// Error type for showErrorToast
interface ErrorResponse {
    response?: {
        data?: {
            error?: string;
            details?: string;
        };
    };
    message?: string;
}

// --- Push to Queue Types and API Methods ---

// Type definitions for push to queue functionality
export interface ExperimentDesignPushToQueueData {
    updated_by: string;
    status?: string | null;
    count?: number; // Number of experiments to push (optional, defaults to all remaining)
}

export interface PushToQueueResponse {
    message: string;
    details: {
        designs_processed: number;
        starting_position: number;
        total_entries_created: number;
        remaining_entries_for_design?: number; // How many more experiments can be pushed for this design
    };
}

/**
 * Push an experiment design to the queue.
 * If the design has children, all children will be pushed instead of the parent.
 * Only leaf designs (designs without children) can actually be queued.
 * @param designId - The experiment design ID
 * @param data - Push data including optional count parameter
 */
export const pushExperimentDesignToQueue = async (designId: number, data: Omit<ExperimentDesignPushToQueueData, 'updated_by'>, userEmail: string): Promise<PushToQueueResponse> => {
    try {
        // Inject user's email as updated_by
        const dataWithUser: ExperimentDesignPushToQueueData = {
            ...data,
            updated_by: userEmail,
        };

        console.log('Pushing experiment design to queue:', designId, dataWithUser);
        const response = await api.post<PushToQueueResponse>(`/experiment_designs/${designId}/push_to_queue`, dataWithUser);
        return response.data;
    } catch (error) {
        console.error('Error pushing experiment design to queue:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to push experiment design to queue",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

// --- API Methods ---
export const getAllExperimentDesigns = async (): Promise<ExperimentDesignFull[]> => {
    try {
        const response = await api.get<ExperimentDesignFull[]>('/experiment_designs');
        return response.data;
    } catch (error) {
        console.error('Error fetching experiment designs:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to fetch experiment designs",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

const getExperimentDesignById = async (id: number): Promise<ExperimentDesignFull> => {
    try {
        const response = await api.get<ExperimentDesignFull>(`/experiment_designs/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching experiment design:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to fetch experiment design",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

export const createExperimentDesign = async (designData: Omit<ExperimentDesignCreate, 'owner' | 'updated_by'>, userEmail: string): Promise<ExperimentDesignFull> => {
    try {
        // Inject user's email as updated_by and owner
        const dataWithUser: ExperimentDesignCreate = {
            ...designData,
            updated_by: userEmail,
            owner: userEmail,
        };
        console.log('Creating experiment design with data:', dataWithUser);
        const response = await api.post<ExperimentDesignFull>(`/experiment_designs`, dataWithUser);
        return response.data;
    } catch (error) {
        console.error('Error creating experiment design:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to create experiment design",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

export const createExperimentDesignWithChildren = async (designData: ExperimentDesignCreateWithChildren, userEmail: string): Promise<ExperimentDesignFull> => {
    try {
        // Inject user's email as updated_by and owner
        const dataWithUser = {
            ...designData,
            updated_by: userEmail,
            owner: userEmail,
            children: designData.children?.map(child => ({
                ...child,
                updated_by: userEmail,
                owner: userEmail,
            })),
        };
        console.log('Creating experiment design with children with data:', dataWithUser);
        const response = await api.post<ExperimentDesignFull>(`/experiment_designs`, dataWithUser);
        return response.data;
    } catch (error) {
        console.error('Error creating experiment design with children:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to create experiment design with children",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

export const updateExperimentDesign = async (id: number, designData: Omit<ExperimentDesignUpdate, 'updated_by'>, userEmail: string): Promise<ExperimentDesignFull> => {
    try {
        // Inject user's email as updated_by
        const dataWithUser: ExperimentDesignUpdate = {
            ...designData,
            updated_by: userEmail,
        };
        console.log('Updating experiment design with data:', dataWithUser);
        const response = await api.put<ExperimentDesignFull>(`/experiment_designs/${id}`, dataWithUser);
        return response.data;
    } catch (error) {
        console.error('Error updating experiment design:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to update experiment design",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

export const deleteExperimentDesign = async (id: number): Promise<void> => {
    try {
        await api.delete(`/experiment_designs/${id}`);
    } catch (error) {
        console.error('Error deleting experiment design:', error);
        showErrorToast({
            response: {
                data: {
                    error: "Failed to delete experiment design",
                    details: error instanceof Error ? error.message : "Unknown error occurred"
                }
            }
        });
        throw error;
    }
};

// --- Hierarchical Helper Functions ---

/**
 * Create a child experiment design for a given parent
 */
const createChildExperimentDesign = async (parentId: number, childData: Omit<ExperimentDesignCreate, 'parent_experiment_design_id' | 'owner' | 'updated_by'>, userEmail: string): Promise<ExperimentDesignFull> => {
    const createData: Omit<ExperimentDesignCreate, 'owner' | 'updated_by'> = {
        ...childData,
        parent_experiment_design_id: parentId,
    };
    // This will now automatically inject the user email as well
    return createExperimentDesign(createData, userEmail);
};

/**
 * Get all children of a specific parent design
 */
const getChildrenOfDesign = async (parentId: number): Promise<ExperimentDesignFull[]> => {
    try {
        const allDesigns = await getAllExperimentDesigns();
        return allDesigns.filter(design => design.parent_experiment_design_id === parentId);
    } catch (error) {
        console.error('Error fetching child designs:', error);
        throw error;
    }
};

/**
 * Transform flat list of designs into hierarchical structure
 */
export const buildHierarchicalDesigns = (designs: ExperimentDesignFull[]): ExperimentDesignFull[] => {
    const designMap = new Map<number, ExperimentDesignFull>();
    const rootDesigns: ExperimentDesignFull[] = [];

    console.log('Building hierarchical designs from:', designs.map(d => ({ id: d.experiment_design_id, parent_id: d.parent_experiment_design_id, name: d.name })));

    // First pass: create map and initialize children arrays
    designs.forEach(design => {
        designMap.set(design.experiment_design_id, { ...design, children: [] });
    });

    // Second pass: build hierarchy
    designs.forEach(design => {
        const designWithChildren = designMap.get(design.experiment_design_id)!;

        if (design.parent_experiment_design_id === null || design.parent_experiment_design_id === undefined) {
            // Root level design
            console.log(`Adding root design: ${design.name} (id: ${design.experiment_design_id})`);
            rootDesigns.push(designWithChildren);
        } else {
            // Child design - add to parent's children array
            const parent = designMap.get(design.parent_experiment_design_id);
            if (parent) {
                console.log(`Adding child design: ${design.name} (id: ${design.experiment_design_id}) to parent: ${parent.name} (id: ${parent.experiment_design_id})`);
                parent.children!.push(designWithChildren);
            } else {
                // Parent not found, treat as root (orphaned)
                console.log(`Parent not found for design: ${design.name} (id: ${design.experiment_design_id}, parent_id: ${design.parent_experiment_design_id}), treating as root`);
                rootDesigns.push(designWithChildren);
            }
        }
    });

    console.log('Built hierarchical structure:', rootDesigns.map(d => ({
        id: d.experiment_design_id,
        name: d.name,
        children: d.children?.map(c => ({ id: c.experiment_design_id, name: c.name }))
    })));

    return rootDesigns;
};

/**
 * Get all descendant designs (children, grandchildren, etc.) of a parent design
 */
export const getAllDescendants = (design: ExperimentDesignFull): ExperimentDesignFull[] => {
    const descendants: ExperimentDesignFull[] = [];

    if (design.children && design.children.length > 0) {
        design.children.forEach(child => {
            descendants.push(child);
            descendants.push(...getAllDescendants(child));
        });
    }

    return descendants;
}; 